import { useState, type FormEvent } from "react";
import { createContactLead } from "@/lib/contactLeads";

const interestOptions = [
  "Clínica estética",
  "ALÉA Skin (productos)",
  "Cursos ALÉA Academy",
  "Insumos Pro Supply",
  "Sociedad ALÉA",
];

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honey = String(data.get("website_alt") ?? "").trim();
    if (honey) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("sending");
    setFormError("");

    try {
      await createContactLead({
        nombre: String(data.get("nombre") ?? "").trim(),
        telefono: String(data.get("telefono") ?? "").trim(),
        email: String(data.get("email") ?? "").trim(),
        interes: String(data.get("interes") ?? "").trim(),
        mensaje: String(data.get("mensaje") ?? "").trim(),
        source: "website",
      });

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("Ocurrió un problema de conexión. Revisa tu red e intenta de nuevo.");
    }
  };

  return (
    <form
      id="form-contacto-alea"
      data-readdy-form
      onSubmit={handleSubmit}
      className="card-soft rounded-[24px] border border-secondary-200 bg-background-50 p-6 md:p-8"
    >
      <h3 className="font-heading text-2xl font-bold text-foreground-950">Solicita información</h3>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        Cuéntanos qué te interesa y te contactamos por WhatsApp o correo.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-2 text-sm text-foreground-700">
          Nombre
          <input
            type="text"
            name="nombre"
            required
            placeholder="Tu nombre"
            className="min-h-[48px] rounded-[14px] border border-secondary-200 bg-background-50 px-4 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-2 text-sm text-foreground-700">
          Teléfono / WhatsApp
          <input
            type="tel"
            name="telefono"
            placeholder="667 000 0000"
            className="min-h-[48px] rounded-[14px] border border-secondary-200 bg-background-50 px-4 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-2 text-sm text-foreground-700">
          Correo
          <input
            type="email"
            name="email"
            required
            placeholder="tucorreo@email.com"
            className="min-h-[48px] rounded-[14px] border border-secondary-200 bg-background-50 px-4 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-2 text-sm text-foreground-700">
          Me interesa
          <select
            name="interes"
            defaultValue={interestOptions[0]}
            className="min-h-[48px] rounded-[14px] border border-secondary-200 bg-background-50 px-4 text-sm text-foreground-950 focus:border-primary-500 focus:outline-none"
          >
            {interestOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-2 text-sm text-foreground-700 sm:col-span-2">
          Mensaje
          <textarea
            name="mensaje"
            rows={4}
            maxLength={500}
            placeholder="Cuéntanos qué buscas (máximo 500 caracteres)"
            className="rounded-[14px] border border-secondary-200 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none"
          />
        </label>

        <input
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="form-honeypot"
        />
      </div>

      <button
        type="submit"
        disabled={status === "sending"}
        className="cta-primary mt-6 inline-flex min-h-[48px] w-full items-center justify-center gap-2 whitespace-nowrap rounded-[16px] bg-primary-500 px-6 font-label text-[11px] font-semibold uppercase tracking-[0.14em] text-background-50 transition-all duration-300 hover:bg-primary-600 disabled:opacity-60"
      >
        {status === "sending" ? "Enviando..." : "Enviar solicitud"}
        {status !== "sending" ? <i className="ri-send-plane-fill text-base" /> : null}
      </button>

      {status === "success" ? (
        <p className="mt-4 flex items-start gap-2 rounded-[14px] border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-foreground-900">
          <i className="ri-checkbox-circle-fill mt-0.5 text-lg text-accent-700" />
          ¡Gracias! Recibimos tu solicitud y te contactamos muy pronto.
        </p>
      ) : null}

      {status === "error" ? (
        <p className="mt-4 flex items-start gap-2 rounded-[14px] border border-accent-400 bg-accent-100 px-4 py-3 text-sm text-foreground-900">
          <i className="ri-error-warning-fill mt-0.5 text-lg text-accent-800" />
          {formError}
        </p>
      ) : null}
    </form>
  );
}
